import { Button, Form, message, Modal, Space, Spin, Table, Tooltip } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import { calculateColumnsWidth } from 'api/common';
import {
  AuditLog,
  AuditResponse,
  FilterPayload,
  getAuditLog,
  getPaymentList,
  PaymentListData,
  PaymentPagedList,
  retry,
  RetryResponse,
} from 'api/dashboard';
import { LoadingScreen } from 'common/components';
import moment from 'moment';
import { useEffect, useState } from 'react';

type Props = {
  filter: FilterPayload;
  onSubmit: (values: FilterPayload) => Promise<void>;
  setOnlyDigitError: (error: boolean) => void;
};
const PaymentList = ({ filter, setOnlyDigitError }: Props) => {
  let payPagedList: PaymentPagedList = {
    data: [],
    noOfRecords: 0,
    currentPageIndex: 1,
    // Add other required properties based on the `PaymentPagedList` type
  };

  let retryResponse: RetryResponse = {
    data: '',
    message: '',
    responseCode: '',
    status: '',
  };

  let audit: AuditResponse = {
    data: [], // Initialize with an empty array for AuditLogs
  };
  const [_form] = Form.useForm<FilterPayload>();
  const [pagedList, setPagedList] = useState(payPagedList);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isModalLoading, setIsModalLoading] = useState(false);
  const [auditLog, setAuditLog] = useState(audit);
  const [_isRetry, setRetry] = useState(false);
  const [_retryRes, setRetryRes] = useState(retryResponse);

  filter.Username = sessionStorage.getItem('user')?.toString();
  filter.userId = sessionStorage.getItem('userId')?.toString();
  filter.roleId = sessionStorage.getItem('roleId')?.toString();
  filter.pageNo = 1;
  filter.pageSize = 10;
  const isAdmin = sessionStorage.getItem('isAdmin');
  if (isAdmin != 'true') {
    filter.SourceApplicationName = sessionStorage.getItem('source')?.toString();
  }
  // console.log('Filter in Payment Link', filter);

  useEffect(() => {

    // 👈 LOGOUT GUARD CHECK
    const currentToken = sessionStorage.getItem('token');
    const isLogoutPending = sessionStorage.getItem('isLoggingOut') === 'true';

    // EXIT IMMEDIATELY if logged out, token is missing, or logout is in progress
    if (!currentToken || isLogoutPending) {
      return;
    }

    setIsLoading(false);
    setPagedList(payPagedList);

    getPaymentList(filter)
      .then((res) => {
        setPagedList(res.data);
        setOnlyDigitError(false);
        // message.success('Success');
      })
      .finally(() => {
        setIsLoading(true);
      });
  }, [filter, _isRetry]);

  console.log('pagedList in playmnet link', pagedList);

  const handleRetry = (record: PaymentListData) => {
    setIsLoading(true);
    retry(record.PAYMENTREQUESTID)
      .then((res) => {
        setRetryRes(res);

        if (res.responseCode == '200') {
          message.success('Done Successfully!');
          setIsLoading(false);
        } else {
          message.error(res.message);
        }
      })
      .finally(() => {
        setRetry(!_isRetry);
      });
  };

  function getFullDate(date: string | null | undefined): string {
    if (date === null || date === undefined || date.trim() === '') {
      return ''; // Return empty string for null, undefined, or empty dates
    }
    // Parse the date string.
    // Use moment(date) directly for 'YYYY-MM-DD' strings, as moment is smart enough.
    // Then format it to 'DD/MM/YYYY' to remove the time.
    return moment(date).format('DD/MM/YYYY'); // Changed format string
  }

  const columns: ColumnsType<PaymentListData> = [
    {
      key: 'PAYMENTREQUESTID',
      title: 'Request ID',
      dataIndex: 'PAYMENTREQUESTID',
      fixed: 'left',
      ellipsis: true,
      render: (record) => (
        <Tooltip placement="topLeft" title={record}>
          {record}
        </Tooltip>
      ),
      className: '',
    },
    {
      key: 'LANUMBER',
      title: 'Loan Number',
      dataIndex: 'LANUMBER',
      fixed: 'left',
      ellipsis: true,
      render: (record) => (
        <Tooltip placement="topLeft" title={record}>
          {record}
        </Tooltip>
      ),
    },
    {
      key: 'LOANTYPE',
      title: 'Loan Type',
      dataIndex: 'LOANTYPE',
      ellipsis: true,
      render: (record) => (
        <Tooltip placement="topLeft" title={record}>
          {record}
        </Tooltip>
      ),
    },
    {
      key: 'ACCOUNT_ID',
      title: 'Account Id',
      dataIndex: 'ACCOUNT_ID',
      ellipsis: true,
      render: (record) => (
        <Tooltip placement="topLeft" title={record}>
          {record}
        </Tooltip>
      ),
    },
    {
      key: 'SOURCEAPPLICATIONNAME',
      title: 'Source Application Name',
      dataIndex: 'SOURCEAPPLICATIONNAME',
      width: 250,
    },
    {
      key: 'REQUESTPURPOSE',
      title: 'Request Purpose',
      dataIndex: 'REQUESTPURPOSE',
      ellipsis: true,
      render: (record) => (
        <Tooltip placement="topLeft" title={record}>
          {record}
        </Tooltip>
      ),
    },
    {
      key: 'MODE',
      title: 'Mode',
      dataIndex: 'MODE',
      ellipsis: true,
      render: (record) => (
        <Tooltip placement="topLeft" title={record}>
          {record}
        </Tooltip>
      ),
    },
    {
      key: 'IMPACTMODE',
      title: 'Impact Mode',
      dataIndex: 'IMPACTMODE',
      ellipsis: true,
      render: (record) => (
        <Tooltip placement="topLeft" title={record}>
          {record}
        </Tooltip>
      ),
    },
    {
      key: 'GATEWAYNAME',
      title: 'Gateway Name',
      dataIndex: 'GATEWAYNAME',
      ellipsis: true,
      render: (record) => (
        <Tooltip placement="topLeft" title={record}>
          {record}
        </Tooltip>
      ),
    },
    {
      key: 'PAYMENTPURPOSE',
      title: 'Payment Purpose',
      dataIndex: 'PAYMENTPURPOSE',
      ellipsis: true,
      render: (record) => (
        <Tooltip placement="topLeft" title={record}>
          {record}
        </Tooltip>
      ),
    },
    {
      key: 'MERCHANTID',
      title: 'Merchant ID',
      dataIndex: 'MERCHANTID',
      ellipsis: true,
      render: (record) => (
        <Tooltip placement="topLeft" title={record}>
          {record}
        </Tooltip>
      ),
    },
    {
      key: 'AMOUNT',
      title: 'Amount',
      dataIndex: 'AMOUNT',
      ellipsis: true,
      render: (record) => (
        <Tooltip placement="topLeft" title={record}>
          {record}
        </Tooltip>
      ),
    },
    {
      key: 'RequestStatus',
      title: 'Request Status',
      dataIndex: 'RequestStatus',
      ellipsis: true,
      render: (_) => (
        <>
          <Tooltip placement="topLeft" title={_}>
            {_}
          </Tooltip>
          {/* {record.RequestStatus == 'RECEIPT_FAILED' ? setRetry(true) : setRetry(false)} */}
        </>
      ),
    },
    {
      key: 'NEW_STATUS',
      title: 'New Status',
      dataIndex: 'NEW_STATUS',
      ellipsis: true,
      render: (_) => (
        <>
          <Tooltip placement="topLeft" title={_}>
            {_}
          </Tooltip>
          {/* {record.NEW_STATUS == 'Initiated' ? setRetry(true) : setRetry(false)} */}
        </>
      ),
    },
    {
      key: 'PAYMENTREF',
      title: 'PaymentRef',
      dataIndex: 'PAYMENTREF',
      ellipsis: true,
      render: (record) => (
        <Tooltip placement="topLeft" title={record}>
          {record}
        </Tooltip>
      ),
    },
    {
      key: 'REFTRANSACTION',
      title: 'Ref Transaction',
      dataIndex: 'REFTRANSACTION',
      ellipsis: true,
      render: (record) => (
        <Tooltip placement="topLeft" title={record}>
          {record}
        </Tooltip>
      ),
    },
    {
      key: 'RAZORPAY_ORDER_ID',
      title: 'Order Id',
      dataIndex: 'RAZORPAY_ORDER_ID',
      ellipsis: true,
      render: (record) => (
        <Tooltip placement="topLeft" title={record}>
          {record}
        </Tooltip>
      ),
    },
    {
      key: 'RAZORPAY_PAYMENT_ID',
      title: 'Payment Id',
      dataIndex: 'RAZORPAY_PAYMENT_ID',
      ellipsis: true,
      render: (record) => (
        <Tooltip placement="topLeft" title={record}>
          {record}
        </Tooltip>
      ),
    },
    {
      key: 'FUNDINGACCOUNT',
      title: 'Funding Account',
      dataIndex: 'FUNDINGACCOUNT',
      ellipsis: true,
      render: (record) => (
        <Tooltip placement="topLeft" title={record}>
          {record}
        </Tooltip>
      ),
    },
    {
      key: 'BANKCODE',
      title: 'Bank Code',
      dataIndex: 'BANKCODE',
      ellipsis: true,
      render: (record) => (
        <Tooltip placement="topLeft" title={record}>
          {record}
        </Tooltip>
      ),
    },
    {
      key: 'CURRENCY',
      title: 'Currency',
      dataIndex: 'CURRENCY',
      ellipsis: true,
      render: (record) => (
        <Tooltip placement="topLeft" title={record}>
          {record}
        </Tooltip>
      ),
    },
    {
      key: 'FAVOURNAME',
      title: 'Favour Name',
      dataIndex: 'FAVOURNAME',
      ellipsis: true,
      render: (record) => (
        <Tooltip placement="topLeft" title={record}>
          {record}
        </Tooltip>
      ),
    },
    {
      key: 'VERIFICATIONREASON',
      title: 'Verification Reason',
      dataIndex: 'VERIFICATIONREASON',
      ellipsis: true,
      render: (record) => (
        <Tooltip placement="topLeft" title={record}>
          {record}
        </Tooltip>
      ),
    },
    {
      key: 'AGENTREJECTREMARKS',
      title: 'Agent Reject Remarks',
      dataIndex: 'AGENTREJECTREMARKS',
      ellipsis: true,
      render: (record) => (
        <Tooltip placement="topLeft" title={record}>
          {record}
        </Tooltip>
      ),
    },
    {
      key: 'PAYMENTREQUESTDATETIME',
      title: 'Request Date & Time',
      dataIndex: 'PAYMENTREQUESTDATETIME',
      ellipsis: true,
      render: (date: string) => getCurrentDate(date),
    },
    {
      key: 'PENNANTRECEIPTDATE',
      title: 'Receipt Date & Time',
      dataIndex: 'PENNANTRECEIPTDATE',
      ellipsis: true,
      //render: (date: string) => getFullDate(date),
      render: (date: string | null | undefined) => { // Allow for null or undefined as well
        if (!date || date.trim() === '') { // Check if the date string is empty, null, or undefined
          return ''; // Or return '' or '-' or any placeholder you prefer
        }
        return getCurrentDate(date); // Only call getFullDate if there's a valid date string
      },
    },
    {
      key: 'PENNANTRECEIPTNO',
      title: 'Pennant Receipt No',
      dataIndex: 'PENNANTRECEIPTNO',
      ellipsis: true,
      render: (record) => (
        <Tooltip placement="topLeft" title={record}>
          {record}
        </Tooltip>
      ),
    },
    {
      key: 'AUTH_CODE',
      title: 'AuthCode',
      dataIndex: 'AUTH_CODE',
      ellipsis: true,
      render: (record) => (
        <Tooltip placement="topLeft" title={record}>
          {record}
        </Tooltip>
      ),
    },
    {
      key: 'BYAGENT',
      title: 'By Agent',
      dataIndex: 'BYAGENT',
      ellipsis: true,
      render: (record) => (
        <Tooltip placement="topLeft" title={record}>
          {record}
        </Tooltip>
      ),
    },
    {
      key: 'REMARKS',
      title: 'Remark',
      dataIndex: 'REMARKS',
      ellipsis: true,
      render: (record) => (
        <Tooltip placement="topLeft" title={record}>
          {record}
        </Tooltip>
      ),
    },
    {
      key: 'PAYMENTREQUESTERROR',
      title: 'Payment Request Status',
      dataIndex: 'PAYMENTREQUESTERROR',
      ellipsis: true,
      render: (record) => (
        <Tooltip placement="topLeft" title={record}>
          {record}
        </Tooltip>
      ),
    },
    {
      key: 'PENNANTERRORDESC',
      title: 'Receipt Status',
      dataIndex: 'PENNANTERRORDESC',
      ellipsis: true,
      render: (record) => (
        <Tooltip placement="topLeft" title={record}>
          {record}
        </Tooltip>
      ),
    },
    {
      key: 'WEBHOOKERROR',
      title: 'Webhook Status',
      dataIndex: 'WEBHOOKERROR',
      ellipsis: true,
      render: (record) => (
        <Tooltip placement="topLeft" title={record}>
          {record}
        </Tooltip>
      ),
    },
    {
      key: 'FORECLOSURE_REQUEST_DATE',
      title: 'Foreclosure Request Date',
      dataIndex: 'foreclosureRequestDate',
      ellipsis: true,
      render: (date: string) =>
        getFullDate(date),
    },
    {
      key: 'FORECLOSURE_ISSUANCE_DATE',
      title: 'Foreclosure Issuance Date',
      dataIndex: 'foreclosureIssuanceDate',
      ellipsis: true,
      render: (date: string) => getFullDate(date),
    },
    {
      key: 'Action',
      title: 'Action',
      dataIndex: 'Action',
      fixed: 'right',
      render: (_text, record) => (
        <>
          <Space direction="vertical">
            <Button
              className="ant-btn ant-btn-primary"
              size="small"
              onClick={() => showModal(record)}
            >
              AUDIT LOG
            </Button>
            {record.RequestStatus == 'RECEIPT_FAILED' ? (
              <Button
                className="ant-btn ant-btn-primary"
                size="small"
                onClick={() => handleRetry(record)}
                style={{
                  marginLeft: '20px',
                }}
              >
                RETRY
              </Button>
            ) : null}
          </Space>
        </>
      ),
    },
  ];

  const auditCols: ColumnsType<AuditLog> = [
    {
      key: 'LANumber',
      title: 'Loan Number',
      dataIndex: 'LANumber',
      width: 80,
    },
    {
      key: 'Event',
      title: 'Event',
      dataIndex: 'Event',
      width: 300,
    },
    {
      key: 'EventDateTime',
      title: 'Event Date & Time',
      dataIndex: 'EventDateTime',
      width: 100,
      render: (date: string) => getCurrentDate(date),
    },
    {
      key: 'ErrorDescription',
      title: 'Error Description',
      dataIndex: 'ErrorDescription',
      width: 100,
    },
  ];
  // let payfilter: FilterPayload = {};
  if (!pagedList || !isLoading) {
    return <LoadingScreen />;
  }

  const showModal = (record: PaymentListData) => {
    // setIsLoading(false);
    setIsModalLoading(true);
    getAuditLog(record.PAYMENTREQUESTID, false).then((res) => {
      setAuditLog(res);
      setIsModalOpen(true);
      // setIsLoading(true);
      setIsModalLoading(false);
    });
  };

  function getCurrentDate(date: string) {
    if (date != null) {
      let dateMoment = moment(new Date(date)).format('DD/MM/YYYY HH:mm:ss');
      return dateMoment;
    }
  }

  // const downloadAudit = (record: PaymentListData) => {
  //   getAuditLog(record.PAYMENTREQUESTID, true).then((res) => {
  //     // setAuditLog(res);
  //     let settings = {
  //       fileName: 'RequestResponse_' + record.PAYMENTREQUESTID, // Name of the resulting spreadsheet
  //       extraLength: 3, // A bigger number means that columns will be wider
  //       writeMode: 'writeFile', // The available parameters are 'WriteFile' and 'write'. This setting is optional. Useful in such cases https://docs.sheetjs.com/docs/solutions/output#example-remote-file
  //       writeOptions: {}, // Style options from https://github.com/SheetJS/sheetjs#writing-options
  //       RTL: true, // Display the columns from right-to-left (the default value is false)
  //     };
  //     let data = [
  //       {
  //         sheet: 'Sheet 1',
  //         columns: [
  //           { label: 'PaymentRequestID', value: 'PaymentRequestID' },
  //           { label: 'JsonFormate', value: 'JsonFormate' },
  //           { label: 'JsonResult', value: 'JsonResult' },
  //         ],
  //         content: res.data,
  //       },
  //     ];

  //     xlsx(data, settings);
  //   });
  // };

  const handleOk = () => {
    setIsModalOpen(false);
  };

  const handleCancel = () => {
    setIsModalOpen(false);
  };

  const handleChange = (_pagination: any, _filters: any, sorter: any) => {
    setIsLoading(false);

    if (sorter.hasOwnProperty('column')) {
      // params.order = { field: sorter.field, dir: sorter.order };
    }
    filter.Username = sessionStorage.getItem('user')?.toString();
    if (isAdmin != 'true') {
      filter.SourceApplicationName = filter.SourceApplicationName;
    }

    filter.pageNo = _pagination.current;
    filter.pageSize = _pagination.pageSize;

    getPaymentList(filter).then((res) => {
      setPagedList(res.data);
      setIsLoading(true);
    });
  };

  const maxWidthPerCell = 1000;

  const dataTable = calculateColumnsWidth(columns, pagedList.data, maxWidthPerCell);
  const tableHeight = 500;
  const rowKey = 'ROWNUM';

  return (
    <>
      <Spin spinning={isModalLoading}>
        <Table
          columns={dataTable.columns}
          dataSource={dataTable.source}
          rowKey={rowKey}
          scroll={{ x: dataTable.tableWidth, y: tableHeight }}
          pagination={{
            pageSize: 10,
            total: pagedList.noOfRecords,
            current: pagedList.currentPageIndex == 0 ? 1 : pagedList.currentPageIndex,
          }}
          onChange={handleChange}
        />
      </Spin>
      ;
      <Modal
        title="Audit Log"
        open={isModalOpen}
        onOk={handleOk}
        onCancel={handleCancel}
        width="auto"
        footer={[<div></div>]}
      >
        <div className="">
          {isModalLoading ? (
            <LoadingScreen />
          ) : (
            <Table<AuditLog>
              dataSource={auditLog?.data}
              columns={auditCols}
              scroll={{ x: 2000, y: 500 }}
              pagination={false}
            />
          )}
        </div>
      </Modal>
    </>
  );
};

export default PaymentList;
